import {
  WaAfterHideEvent,
  WaAfterShowEvent,
  WaHideEvent,
  WaShowEvent
} from "./chunk.B4WMB4NW.js";
import {
  to
} from "./chunk.EH2PNIGX.js";
import {
  size_default
} from "./chunk.SI24X5IH.js";
import {
  waitForEvent
} from "./chunk.FHRHJUC7.js";
import {
  animateWithClass
} from "./chunk.47BWNU44.js";
import {
  Rt
} from "./chunk.HBRIRAGU.js";
import {
  watch
} from "./chunk.2NT6DI7B.js";
import {
  WebAwesomeElement,
  e,
  n,
  t
} from "./chunk.LVUMIIKQ.js";
import {
  ke
} from "./chunk.S3625EU6.js";
import {
  __decorateClass
} from "./chunk.SZE6ONST.js";

// src/components/dropdown/dropdown.css
var dropdown_default = ":host {\n  --box-shadow: var(--wa-shadow-m);\n\n  display: inline-block;\n}\n\n.dropdown::part(popup) {\n  z-index: 900;\n}\n\n.dropdown[data-current-placement^='top']::part(popup) {\n  transform-origin: bottom;\n}\n\n.dropdown[data-current-placement^='bottom']::part(popup) {\n  transform-origin: top;\n}\n\n.dropdown[data-current-placement^='left']::part(popup) {\n  transform-origin: right;\n}\n\n.dropdown[data-current-placement^='right']::part(popup) {\n  transform-origin: left;\n}\n\n#trigger {\n  display: block; /* for boundingClientRect */\n}\n\n.panel {\n  font: inherit;\n  box-shadow: var(--box-shadow);\n  border-radius: var(--wa-border-radius-m);\n  pointer-events: none;\n}\n\n.dropdown--open .panel {\n  display: block;\n  pointer-events: all;\n}\n\n/* When users slot a menu, make sure it conforms to the popup's auto-size */\n::slotted(wa-menu) {\n  max-width: var(--auto-size-available-width) !important;\n  max-height: var(--auto-size-available-height) !important;\n}\n";

// src/components/dropdown/dropdown.ts
var WaDropdown = class extends WebAwesomeElement {
  constructor() {
    super(...arguments);
    this.open = false;
    this.placement = "bottom-start";
    this.disabled = false;
    this.stayOpenOnSelect = false;
    this.distance = 0;
    this.skidding = 0;
    this.sync = void 0;
    this.handleKeyDown = (event) => {
      if (this.open && event.key === "Escape") {
        event.stopPropagation();
        this.hide();
        this.focusOnTrigger();
      }
    };
    this.handleDocumentKeyDown = (event) => {
      if (event.key === "Escape" && this.open) {
        event.stopPropagation();
        this.focusOnTrigger();
        this.hide();
        return;
      }
      if (event.key === "Tab") {
        if (this.open && document.activeElement?.tagName.toLowerCase() === "wa-menu-item") {
          event.preventDefault();
          this.hide();
          this.focusOnTrigger();
          return;
        }
        setTimeout(() => {
          const activeElement = this.containingElement?.getRootNode() instanceof ShadowRoot ? document.activeElement?.shadowRoot?.activeElement : document.activeElement;
          if (!this.containingElement || activeElement?.closest(this.containingElement.tagName.toLowerCase()) !== this.containingElement) {
            this.hide();
          }
        });
      }
    };
    this.handleDocumentMouseDown = (event) => {
      const path = event.composedPath();
      if (this.containingElement && !path.includes(this.containingElement)) {
        this.hide();
      }
    };
    this.handlePanelSelect = (event) => {
      const target = event.target;
      if (!this.stayOpenOnSelect && target.tagName.toLowerCase() === "wa-menu") {
        this.hide();
        this.focusOnTrigger();
      }
    };
  }
  connectedCallback() {
    super.connectedCallback();
    if (!this.containingElement) {
      this.containingElement = this;
    }
  }
  firstUpdated() {
    this.panel.hidden = !this.open;
    if (this.open) {
      this.addOpenListeners();
      this.popup.active = true;
    }
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeOpenListeners();
    this.hide();
  }
  focusOnTrigger() {
    const trigger = this.trigger.assignedElements({ flatten: true })[0];
    if (typeof trigger?.focus === "function") {
      trigger.focus();
    }
  }
  getMenu() {
    return this.panel.assignedElements({ flatten: true }).find((el) => el.tagName.toLowerCase() === "wa-menu");
  }
  handleTriggerClick() {
    if (this.open) {
      this.hide();
    } else {
      this.show();
      this.focusOnTrigger();
    }
  }
  async handleTriggerKeyDown(event) {
    if ([" ", "Enter"].includes(event.key)) {
      event.preventDefault();
      this.handleTriggerClick();
      return;
    }
    const menu = this.getMenu();
    if (menu) {
      const menuItems = menu.getAllItems();
      const firstMenuItem = menuItems[0];
      const lastMenuItem = menuItems[menuItems.length - 1];
      if (["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) {
        event.preventDefault();
        if (!this.open) {
          this.show();
          await this.updateComplete;
        }
        if (menuItems.length > 0) {
          this.updateComplete.then(() => {
            if (event.key === "ArrowDown" || event.key === "Home") {
              menu.setCurrentItem(firstMenuItem);
              firstMenuItem.focus();
            }
            if (event.key === "ArrowUp" || event.key === "End") {
              menu.setCurrentItem(lastMenuItem);
              lastMenuItem.focus();
            }
          });
        }
      }
    }
  }
  handleTriggerKeyUp(event) {
    if (event.key === " ") {
      event.preventDefault();
    }
  }
  handleTriggerSlotChange() {
    this.updateAccessibleTrigger();
  }
  updateAccessibleTrigger() {
    const assignedElements = this.trigger.assignedElements({ flatten: true });
    const accessibleTrigger = assignedElements[0];
    let target;
    if (accessibleTrigger) {
      const tagName = accessibleTrigger.tagName.toLowerCase();
      switch (tagName) {
        // Web Awesome buttons have to update the internal button so it's announced correctly by screen readers
        case "wa-button":
        case "wa-icon-button":
          target = accessibleTrigger.button;
          if (target === void 0 || target === null) {
            customElements.whenDefined(tagName).then(async () => {
              await accessibleTrigger.updateComplete;
              this.updateAccessibleTrigger();
            });
            return;
          }
          break;
        default:
          target = accessibleTrigger;
      }
      target.setAttribute("aria-haspopup", "true");
      target.setAttribute("aria-expanded", this.open ? "true" : "false");
    }
  }
  /** Shows the dropdown panel. */
  async show() {
    if (this.open) {
      return void 0;
    }
    this.open = true;
    return waitForEvent(this, "wa-after-show");
  }
  /** Hides the dropdown panel */
  async hide() {
    if (!this.open) {
      return void 0;
    }
    this.open = false;
    return waitForEvent(this, "wa-after-hide");
  }
  /**
   * Instructs the dropdown menu to reposition. Useful when the position or size of the trigger changes when the menu
   * is activated.
   */
  reposition() {
    this.popup.reposition();
  }
  addOpenListeners() {
    this.panel.addEventListener("wa-select", this.handlePanelSelect);
    this.panel.addEventListener("keydown", this.handleKeyDown);
    document.addEventListener("keydown", this.handleDocumentKeyDown);
    document.addEventListener("mousedown", this.handleDocumentMouseDown);
  }
  removeOpenListeners() {
    if (this.panel) {
      this.panel.removeEventListener("wa-select", this.handlePanelSelect);
      this.panel.removeEventListener("keydown", this.handleKeyDown);
    }
    document.removeEventListener("keydown", this.handleDocumentKeyDown);
    document.removeEventListener("mousedown", this.handleDocumentMouseDown);
  }
  async handleOpenChange() {
    if (this.disabled) {
      this.open = false;
      return;
    }
    this.updateAccessibleTrigger();
    if (this.open) {
      const waShowEvent = new WaShowEvent();
      this.dispatchEvent(waShowEvent);
      if (waShowEvent.defaultPrevented) {
        this.open = false;
        return;
      }
      this.addOpenListeners();
      this.panel.hidden = false;
      this.popup.active = true;
      await animateWithClass(this.popup.popup, "show-with-scale");
      this.dispatchEvent(new WaAfterShowEvent());
    } else {
      const waHideEvent = new WaHideEvent();
      this.dispatchEvent(waHideEvent);
      if (waHideEvent.defaultPrevented) {
        this.open = true;
        return;
      }
      this.removeOpenListeners();
      await animateWithClass(this.popup.popup, "hide-with-scale");
      this.panel.hidden = true;
      this.popup.active = false;
      this.dispatchEvent(new WaAfterHideEvent());
    }
  }
  render() {
    return ke`
      <slot
        name="trigger"
        id="trigger"
        part="trigger"
        @click=${this.handleTriggerClick}
        @keydown=${this.handleTriggerKeyDown}
        @keyup=${this.handleTriggerKeyUp}
        @slotchange=${this.handleTriggerSlotChange}
      ></slot>
      <wa-popup
        part="base"
        exportparts="popup:base__popup"
        id="dropdown"
        anchor="trigger"
        placement=${this.placement}
        distance=${this.distance}
        skidding=${this.skidding}
        flip
        shift
        auto-size="vertical"
        auto-size-padding="10"
        sync=${to(this.sync ? this.sync : void 0)}
        class=${Rt({
      dropdown: true,
      "dropdown--open": this.open
    })}
      >
        <div aria-hidden=${this.open ? "false" : "true"} aria-labelledby="dropdown">
          <slot part="panel" class="panel"></slot>
        </div>
      </wa-popup>
    `;
  }
};
WaDropdown.shadowStyle = [size_default, dropdown_default];
__decorateClass([
  e(".dropdown")
], WaDropdown.prototype, "popup", 2);
__decorateClass([
  e("#trigger")
], WaDropdown.prototype, "trigger", 2);
__decorateClass([
  e(".panel")
], WaDropdown.prototype, "panel", 2);
__decorateClass([
  n({ type: Boolean, reflect: true })
], WaDropdown.prototype, "open", 2);
__decorateClass([
  n({ reflect: true })
], WaDropdown.prototype, "placement", 2);
__decorateClass([
  n({ type: Boolean, reflect: true })
], WaDropdown.prototype, "disabled", 2);
__decorateClass([
  n({ attribute: "stay-open-on-select", type: Boolean, reflect: true })
], WaDropdown.prototype, "stayOpenOnSelect", 2);
__decorateClass([
  n({ attribute: false })
], WaDropdown.prototype, "containingElement", 2);
__decorateClass([
  n({ type: Number })
], WaDropdown.prototype, "distance", 2);
__decorateClass([
  n({ type: Number })
], WaDropdown.prototype, "skidding", 2);
__decorateClass([
  n({ reflect: true })
], WaDropdown.prototype, "sync", 2);
__decorateClass([
  watch("open", { waitUntilFirstUpdate: true })
], WaDropdown.prototype, "handleOpenChange", 1);
WaDropdown = __decorateClass([
  t("wa-dropdown")
], WaDropdown);

export {
  WaDropdown
};
